import { useState, type FormEvent } from 'react';
import { ApiError, api } from '../api';
import type { User } from '../types';
import { getBranding } from '../branding';
import { Brand, BrandLinks } from './Brand';
import { Icon } from './Icon';

export function Login({
  onAuthenticated,
  onForgotPassword,
  memberOtpEnabled = false,
  memberPasswordBrowserTrust = false,
}: {
  onAuthenticated: (user: User) => void;
  onForgotPassword?: () => void;
  memberOtpEnabled?: boolean;
  memberPasswordBrowserTrust?: boolean;
}) {
  const branding = getBranding();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [challengeId, setChallengeId] = useState('');
  const [code, setCode] = useState('');
  const [notice, setNotice] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [emailMode, setEmailMode] = useState(false);
  const [canTrustBrowser, setCanTrustBrowser] = useState(false);
  const [trustBrowser, setTrustBrowser] = useState(false);
  const emailSignIn = memberOtpEnabled && (!memberPasswordBrowserTrust || emailMode);

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      const normalizedEmail = email.trim().toLowerCase();
      if (challengeId) {
        onAuthenticated(await api.verifyAccountOtp(normalizedEmail, challengeId, code, ...(trustBrowser ? [true] as const : [])));
      } else if (memberPasswordBrowserTrust && !emailSignIn) {
        const response = await api.signInWithBrowserTrust(normalizedEmail, password);
        setPassword('');
        if ('user' in response) onAuthenticated(response.user);
        else { setChallengeId(response.challengeId); setNotice(response.message); setCanTrustBrowser(response.canTrustBrowser); setCode(''); }
      } else if (emailSignIn) {
        const response = await api.requestAccountOtp(normalizedEmail);
        setChallengeId(response.challengeId);
        setNotice(response.message);
        setCode('');
      } else {
        onAuthenticated(await api.login(normalizedEmail, password));
      }
    } catch (reason) {
      setError(reason instanceof ApiError ? reason.message : 'Unable to sign in. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }

  function changeEmail() {
    setChallengeId('');
    setCode('');
    setNotice('');
    setError('');
    setPassword('');
    setCanTrustBrowser(false);
    setTrustBrowser(false);
  }

  return <main className="login-shell">
    <section className="login-panel" aria-labelledby="login-title">
      <Brand className="brand--login" />
      <div className="login-heading"><span className="eyebrow">Video operations</span><h1 id="login-title">Welcome back</h1><p>{challengeId && !emailSignIn ? 'Enter your email code, or use the sign-in link in the same browser.' : emailSignIn ? 'Use the code or sign-in link sent to your account email.' : memberPasswordBrowserTrust ? 'Sign in with your password, or use an email code or sign-in link.' : 'Sign in to your account.'}</p></div>
      <form className="login-form" onSubmit={handleSubmit}>
        {error && <div className="form-alert" role="alert"><Icon name="warning" size={18} /><span>{error}</span></div>}
        {notice && <div className="form-notice" role="status"><Icon name="check" size={18} /><span>{notice}</span></div>}
        <label>Email address<input type="email" autoComplete="email" value={email} onChange={(event) => setEmail(event.target.value)} placeholder="you@example.com" required autoFocus={!challengeId} readOnly={memberOtpEnabled && Boolean(challengeId)} /></label>
        {memberOtpEnabled && challengeId ? <label>Six-digit code<input className="otp-code-input" type="text" inputMode="numeric" autoComplete="one-time-code" pattern="[0-9]{6}" maxLength={6} value={code} onChange={(event) => setCode(event.target.value.replace(/\D/g, '').slice(0, 6))} placeholder="000000" required autoFocus /></label> : null}
        {!emailSignIn && !challengeId && <label>Password<input type="password" autoComplete="current-password" value={password} onChange={(event) => setPassword(event.target.value)} placeholder="Enter your password" required /></label>}
        {memberPasswordBrowserTrust && challengeId && canTrustBrowser && <label className="checkbox-field"><input className="control-choice" type="checkbox" checked={trustBrowser} disabled={submitting} onChange={event => setTrustBrowser(event.target.checked)} /><span><strong>Trust this browser for 30 days</strong><small>Skip email verification on password sign-in. Do not use on a shared computer.</small></span></label>}
        <button className="button button--primary button--large" type="submit" disabled={submitting || (Boolean(challengeId) && code.length !== 6)}>{submitting ? <><span className="spinner" />{emailSignIn && !challengeId ? 'Sending code…' : 'Signing in…'}</> : emailSignIn && !challengeId ? 'Email me a code' : 'Sign in'}</button>
        {challengeId && <button className="text-button login-forgot" type="button" disabled={submitting} onClick={changeEmail}>Use a different email</button>}
        {memberPasswordBrowserTrust && !challengeId && <button className="text-button login-forgot" type="button" disabled={submitting} onClick={() => { changeEmail(); setEmailMode(!emailMode); }}>{emailMode ? 'Use password instead' : 'Use a code or sign-in link'}</button>}
        {(!memberOtpEnabled || memberPasswordBrowserTrust) && !challengeId && onForgotPassword && <button className="text-button login-forgot" type="button" onClick={onForgotPassword}>Forgot your password?</button>}
      </form>
      <p className="login-security">{challengeId || emailSignIn ? 'The code and link expire after 10 minutes. Using either invalidates both.' : memberPasswordBrowserTrust ? 'A new browser needs email verification. Trusted Member browsers still require your password when the session expires.' : 'Protected by a secure, HTTP-only session cookie.'}</p>
      <BrandLinks />
    </section>
    <aside className="login-art" aria-hidden="true">
      <img className="login-art__image" src={branding.assets.loginIllustration} alt="" />
    </aside>
  </main>;
}
